import { useState, useEffect } from "react";
import { inquiriesAPI } from "../../services/api";
import {
  Card, SectionHeader, Badge, StatusBadge, EmptyState, LoadingPage,
} from "../../components/common/UI";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";

const STATUSES = ["pending", "reviewed", "contacted", "closed"];
const STATUS_LABELS = { pending: "Pending", reviewed: "Reviewed", contacted: "Contacted", closed: "Closed" };
const BADGE_VARIANT = { pending: "yellow", reviewed: "blue", contacted: "green", closed: "gray" };

export default function InquiryManagement() {
  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [openIds, setOpenIds]       = useState(new Set());
  const [toast, setToast]           = useState({ msg: "", show: false });

  useEffect(() => {
    inquiriesAPI.grouped()
      .then((res) => {
        const d = res.data?.data?.inquiries || res.data?.data || res.data?.inquiries || (Array.isArray(res.data) ? res.data : []);
        setData(Array.isArray(d) ? d : []);
        setOpenIds(new Set((Array.isArray(d) ? d : []).map((p) => p?.product_id).filter(Boolean)));
      })
      .catch((err) => setError(err.response?.data?.msg || err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, []);

  function toggleProduct(pid) {
    setOpenIds((prev) => {
      const n = new Set(prev);
      n.has(pid) ? n.delete(pid) : n.add(pid);
      return n;
    });
  }

  function showToast(msg) {
    setToast({ msg, show: true });
    setTimeout(() => setToast((p) => ({ ...p, show: false })), 2200);
  }

  async function handleStatusChange(inquiryId, newStatus) {
    setLoadingIds((prev) => new Set(prev).add(inquiryId));
    try {
      await inquiriesAPI.updateStatus(inquiryId, newStatus);
      setData((prev) =>
        prev.map((product) => ({
          ...product,
          inquiries: (product.inquiries || []).map((inq) =>
            inq.inquiry_id === inquiryId ? { ...inq, status: newStatus } : inq
          ),
        }))
      );
      showToast(`Status updated → ${STATUS_LABELS[newStatus]}`);
    } catch (err) {
      showToast(`Failed: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoadingIds((prev) => { const n = new Set(prev); n.delete(inquiryId); return n; });
    }
  }

  const allInquiries   = Array.isArray(data) ? data.flatMap((p) => p?.inquiries || []) : [];
  const totalInquiries = allInquiries.length;
  const totalProducts  = data.length;
  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = allInquiries.filter((r) => r.status === s).length;
    return acc;
  }, {});

  if (loading) return <LoadingPage />;
  if (error)   return (
    <div className="animate-in">
      <SectionHeader title="Inquiries" subtitle="Error loading data" />
      <Card><p className="text-sm text-red-500 p-4">{error}</p></Card>
    </div>
  );

  return (
    <div className="animate-in space-y-5">

      {/* ── Header ── */}
      <SectionHeader
        title="Inquiries"
        subtitle={`${totalInquiries} ${totalInquiries === 1 ? "inquiry" : "inquiries"} across ${totalProducts} products`}
      />

      {/* ── Stat Pills ── */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-50 border border-surface-200 text-sm">
          <span className="text-surface-500 font-medium">Total</span>
          <span className="font-bold text-surface-900">{totalInquiries}</span>
        </div>
        {STATUSES.map((s) => (
          <div key={s} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-50 border border-surface-200 text-sm">
            <span className="text-surface-500 font-medium">{STATUS_LABELS[s]}</span>
            <Badge variant={BADGE_VARIANT[s]}>{counts[s]}</Badge>
          </div>
        ))}
      </div>

      {/* ── Product Blocks ── */}
      {data.length === 0 ? (
        <Card>
          <EmptyState icon={ChatBubbleLeftRightIcon} title="No inquiries found" />
        </Card>
      ) : (
        <div className="space-y-3">
          {data.map((product) => {
            const isOpen = openIds.has(product.product_id);
            return (
              <Card key={product.product_id} className="overflow-hidden p-0">

                {/* ── Product Header ── */}
                <button
                  type="button"
                  onClick={() => toggleProduct(product.product_id)}
                  className="w-full flex items-center justify-between px-5 py-3.5 bg-surface-50 hover:bg-surface-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 overflow-hidden rounded-lg border border-surface-200 bg-white flex items-center justify-center flex-shrink-0">
                      {product.product_image ? (
                        <img src={product.product_image} alt={product.product_name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-surface-100 text-surface-400">
                          📦
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-surface-900">{product.product_name}</p>
                      <p className="text-xs text-surface-400 mt-0.5">{product.product_slug}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="blue">
                      {(product.inquiries || []).length} {(product.inquiries || []).length === 1 ? "inquiry" : "inquiries"}
                    </Badge>
                    {STATUSES.map((s) => {
                      const c = (product.inquiries || []).filter((i) => i.status === s).length;
                      if (!c) return null;
                      return <Badge key={s} variant={BADGE_VARIANT[s]}>{STATUS_LABELS[s]}</Badge>;
                    })}
                    <span className={`text-xs text-surface-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>▼</span>
                  </div>
                </button>

                {/* ── Inquiry Table ── */}
                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Description</th>
                          <th>Phone</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Update</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(product.inquiries || []).map((inq) => {
                          const isLoading = loadingIds.has(inq.inquiry_id);
                          return (
                            <tr key={inq.inquiry_id}>
                              {/* User */}
                              <td>
                                <div className="flex items-center gap-2">
                                  <div className="h-7 w-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                    U{inq.user_id}
                                  </div>
                                  <span className="text-xs text-surface-400">#{inq.inquiry_id}</span>
                                </div>
                              </td>

                              {/* Description */}
                              <td className="max-w-xs">
                                <p className="text-sm text-surface-700 truncate" title={inq.description}>
                                  {inq.description}
                                </p>
                              </td>

                              {/* Phone */}
                              <td className="text-sm text-surface-900 font-mono">{inq.phone}</td>

                              {/* Date */}
                              <td>
                                <p className="text-xs text-surface-600">
                                  {new Date(inq.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
                                </p>
                                <p className="text-xs text-surface-400">
                                  {new Date(inq.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                </p>
                              </td>

                              {/* Current Status */}
                              <td><StatusBadge status={inq.status} /></td>

                              {/* Update Dropdown */}
                              <td>
                                <select
                                  value={inq.status}
                                  disabled={isLoading}
                                  onChange={(e) => handleStatusChange(inq.inquiry_id, e.target.value)}
                                  className="input text-xs py-1 px-2 h-auto"
                                  style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? "not-allowed" : "pointer" }}
                                >
                                  {STATUSES.map((s) => (
                                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Toast ── */}
      <div className={`fixed bottom-6 right-6 bg-surface-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg transition-all duration-200 pointer-events-none z-50 ${toast.show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
        {toast.msg}
      </div>

    </div>
  );
}